import type { TrackerAvailabilityState } from './tracker';
import type { AiAssignSuggestion, AiAssignChoice } from './pendingRequests';
import type { RequestSuggestion } from './suggestions';

// ============================================================================
// Contributor pseudonymization for the AI-assign payload
//
// The assign flow sends contributor identity to ai-agent-ondemand-mcp: a
// trimmed availability state plus per-request suggestion lists. Real names and
// emails are PII we deliberately do not hand to the agent. Every contributor
// identity becomes a per-call token (salted SHA-256 of the email, truncated),
// so the model can still correlate a suggestion with its availability row and
// echo the token back. The frontend holds the only map back to real values.
// ============================================================================

export interface PseudonymizedContributor {
  id: string;
  name: string;
  email: string;
}

/**
 * Serialized token → real identity map. Sent to the trusted backend so it can
 * de-obfuscate the ai-choice event it persists. The map is per-call (fresh salt)
 * and is never handed to the model or stored in the event document.
 */
export type RevealMap = Record<string, PseudonymizedContributor>;

export interface ContributorPseudonymizer {
  /** Token for a contributor email/id — deterministic within one instance. */
  tokenFor(email: string): string;
  /** Real contributor for a token, if this instance created it. */
  reveal(token: string): PseudonymizedContributor | undefined;
  /** Replace every token this instance issued with its real contributor name. */
  deobfuscateText(text: string): string;
  /** Serialized token → real identity map for the backend to reverse events. */
  toRevealMap(): RevealMap;
}

const TOKEN_LENGTH = 16;
const TOKEN_COLLISION_LIMIT = 3;

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

async function sha256Hex(input: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return toHex(new Uint8Array(digest));
}

/**
 * Build a per-call pseudonymizer over the given contributors. The salt is fresh
 * per call, so the same contributor has a different token on every request;
 * without the salt the tokens cannot be traced back to real identities.
 */
export async function createContributorPseudonymizer(
  contributors: Array<{ id: string; name?: string; email?: string }>
): Promise<ContributorPseudonymizer> {
  const salt = toHex(crypto.getRandomValues(new Uint8Array(16)));
  const tokens = new Map<string, string>();
  const reverse = new Map<string, PseudonymizedContributor>();

  for (const c of contributors) {
    const email = c.email ?? c.id;
    if (!email || tokens.has(email)) continue;
    let token = (await sha256Hex(salt + email)).slice(0, TOKEN_LENGTH);
    let suffix = 0;
    while (reverse.has(token) && suffix < TOKEN_COLLISION_LIMIT) {
      suffix += 1;
      token = (await sha256Hex(`${salt}${email}:${suffix}`)).slice(0, TOKEN_LENGTH);
    }
    tokens.set(email, token);
    reverse.set(token, { id: c.id, name: c.name ?? c.id, email });
  }

  return {
    tokenFor(email: string): string {
      const token = tokens.get(email);
      if (!token) throw new Error(`No pseudonym exists for contributor ${email}`);
      return token;
    },
    reveal(token: string): PseudonymizedContributor | undefined {
      return reverse.get(token);
    },
    deobfuscateText(text: string): string {
      let out = text;
      for (const [token, contributor] of reverse) {
        out = out.split(token).join(contributor.name);
      }
      return out;
    },
    toRevealMap(): RevealMap {
      const out: RevealMap = {};
      for (const [token, contributor] of reverse) {
        out[token] = contributor;
      }
      return out;
    },
  };
}

/**
 * Remap a contributor-keyed map to tokens. Maps are keyed by contributor id in
 * the tracker's db-get-state payload; resolve each id to its token via the
 * id→token index, falling back to a direct email lookup for maps already keyed
 * by email. Any key that resolves to no token is dropped — it would leak an
 * identity.
 */
function remapKeys<T>(
  map: Record<string, T> | undefined,
  idToToken: Map<string, string>,
  p: ContributorPseudonymizer
): Record<string, T> | undefined {
  if (!map) return undefined;
  const out: Record<string, T> = {};
  for (const [key, value] of Object.entries(map)) {
    let token = idToToken.get(key);
    if (!token) {
      try {
        token = p.tokenFor(key);
      } catch {
        continue;
      }
    }
    out[token] = value;
  }
  return out;
}

/** Obfuscate a trimmed availability state: ids/names become tokens, emails drop. */
export function pseudonymizeAvailabilityState(
  state: TrackerAvailabilityState,
  p: ContributorPseudonymizer
): TrackerAvailabilityState {
  const idToToken = new Map<string, string>();
  for (const c of state.contributors ?? []) {
    try {
      idToToken.set(c.id, p.tokenFor(c.email ?? c.id));
    } catch {
      // An untokenized contributor would leak — skip it.
    }
  }
  return {
    ...state,
    contributors: (state.contributors ?? []).flatMap(c => {
      const email = c.email ?? c.id;
      if (!email) return [];
      try {
        const token = p.tokenFor(email);
        return [
          {
            id: token,
            name: token,
            region: c.region,
            displayOrder: c.displayOrder,
            isOutOfOffice: c.isOutOfOffice,
            lastUpdated: c.lastUpdated,
          },
        ];
      } catch {
        return [];
      }
    }),
    availability: remapKeys(state.availability, idToToken, p) as TrackerAvailabilityState['availability'],
    dateAvailability: remapKeys(state.dateAvailability, idToToken, p) as TrackerAvailabilityState['dateAvailability'],
    weeklyScheduleToday: remapKeys(state.weeklyScheduleToday, idToToken, p) as TrackerAvailabilityState['weeklyScheduleToday'],
    experienceLevels: remapKeys(state.experienceLevels, idToToken, p) as TrackerAvailabilityState['experienceLevels'],
  };
}

/**
 * Obfuscate a suggestion: scName and scEmail both become the contributor's
 * per-call token. The token is the salted hash of the email, so the model sees
 * no PII yet can echo the email back; the frontend's reveal map restores it.
 */
export function pseudonymizeSuggestion(
  s: RequestSuggestion,
  p: ContributorPseudonymizer
): AiAssignSuggestion {
  const token = p.tokenFor(s.email ?? s.contributorId);
  return {
    scName: token,
    scEmail: token,
    full: s.full,
    maxLevel: s.maxLevel,
    inProgressCount: s.inProgressCount,
  };
}

/** Map a returned choice's token back to the real contributor and de-obfuscate the justification. */
export function revealChoice(choice: AiAssignChoice, p: ContributorPseudonymizer): AiAssignChoice {
  const real = p.reveal(choice.scName);
  if (!real) throw new Error(`AI returned an unknown pseudonym: ${choice.scName}`);
  return {
    ...choice,
    scName: real.name,
    scEmail: real.email,
    reason: p.deobfuscateText(choice.reason),
  };
}
